import type { IssueForm, RepositorySettings } from './types';
import { FIELD_ID_PATTERN, isSafeFilename, normalizeStringList } from './utils';

export type ValidationSeverity = 'error' | 'warning';

export interface ValidationIssue {
  severity: ValidationSeverity;
  code: string;
  message: string;
  path: string;
  fieldIndex?: number;
  fieldUid?: string;
}

export interface ValidationResult {
  issues: ValidationIssue[];
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  valid: boolean;
  isValid: boolean;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finish(issues: ValidationIssue[]): ValidationResult {
  const errors = issues.filter((issue) => issue.severity === 'error');
  const warnings = issues.filter((issue) => issue.severity === 'warning');
  return {
    issues,
    errors,
    warnings,
    valid: errors.length === 0,
    isValid: errors.length === 0,
  };
}

function normalizedListDiffers(value: unknown): boolean {
  if (!Array.isArray(value) || !value.every((entry) => typeof entry === 'string')) return false;
  const normalized = normalizeStringList(value);
  return (
    normalized.length !== value.length || normalized.some((entry, index) => entry !== value[index])
  );
}

function fieldContext(field: UnknownRecord, fieldIndex: number) {
  const uid = typeof field.uid === 'string' ? field.uid : undefined;
  return uid === undefined ? { fieldIndex } : { fieldIndex, fieldUid: uid };
}

export function validateIssueForm(form: IssueForm): ValidationResult {
  const issues: ValidationIssue[] = [];
  const raw = form as unknown as UnknownRecord;

  const add = (issue: ValidationIssue) => issues.push(issue);

  if (typeof raw.filename !== 'string' || !isSafeFilename(raw.filename)) {
    add({
      severity: 'error',
      code: 'filename.invalid',
      message: 'Filename must use safe characters and end in .yml or .yaml.',
      path: 'filename',
    });
  }
  if (typeof raw.name !== 'string' || raw.name.trim() === '') {
    add({
      severity: 'error',
      code: 'name.required',
      message: 'Name is required.',
      path: 'name',
    });
  }
  if (typeof raw.description !== 'string' || raw.description.trim() === '') {
    add({
      severity: 'error',
      code: 'description.required',
      message: 'Description is required.',
      path: 'description',
    });
  }
  if (typeof raw.title !== 'string') {
    add({
      severity: 'error',
      code: 'title.invalid',
      message: 'Title prefix must be a string.',
      path: 'title',
    });
  }

  for (const key of ['labels', 'assignees'] as const) {
    const value = raw[key];
    if (!Array.isArray(value) || !value.every((entry) => typeof entry === 'string')) {
      add({
        severity: 'error',
        code: `${key}.invalid`,
        message: `${key === 'labels' ? 'Labels' : 'Assignees'} must be a list of strings.`,
        path: key,
      });
    } else if (normalizedListDiffers(value)) {
      add({
        severity: 'warning',
        code: `${key}.normalized`,
        message: `Empty and duplicate ${key} will be removed when exporting.`,
        path: key,
      });
    }
  }

  if (!Array.isArray(raw.body) || raw.body.length === 0) {
    add({
      severity: 'error',
      code: 'body.required',
      message: 'The form must contain at least one field.',
      path: 'body',
    });
    return finish(issues);
  }

  const usedIds = new Map<string, number>();
  (raw.body as unknown[]).forEach((rawField, fieldIndex) => {
    const path = `body[${fieldIndex}]`;
    if (!isRecord(rawField)) {
      add({
        severity: 'error',
        code: 'field.invalid',
        message: 'Each body item must be a field object.',
        path,
        fieldIndex,
      });
      return;
    }

    const context = fieldContext(rawField, fieldIndex);
    const type = rawField.type;
    if (
      type !== 'markdown' &&
      type !== 'input' &&
      type !== 'textarea' &&
      type !== 'dropdown' &&
      type !== 'checkboxes'
    ) {
      add({
        severity: 'error',
        code: 'field.type.unknown',
        message: `Unknown field type: ${typeof type === 'string' ? type : String(type)}.`,
        path: `${path}.type`,
        ...context,
      });
      return;
    }

    const attributes = rawField.attributes;
    if (!isRecord(attributes)) {
      add({
        severity: 'error',
        code: 'field.attributes.invalid',
        message: 'Field attributes must be an object.',
        path: `${path}.attributes`,
        ...context,
      });
      return;
    }

    if (type === 'markdown') {
      if (typeof attributes.value !== 'string' || attributes.value.trim() === '') {
        add({
          severity: 'error',
          code: 'markdown.value.required',
          message: 'Markdown content is required.',
          path: `${path}.attributes.value`,
          ...context,
        });
      }
      return;
    }

    const id = rawField.id;
    if (typeof id !== 'string' || id.trim() === '') {
      add({
        severity: 'error',
        code: 'field.id.required',
        message: 'This field requires an ID.',
        path: `${path}.id`,
        ...context,
      });
    } else if (!FIELD_ID_PATTERN.test(id)) {
      add({
        severity: 'error',
        code: 'field.id.invalid',
        message: 'IDs may contain only letters, numbers, hyphens, and underscores.',
        path: `${path}.id`,
        ...context,
      });
    } else {
      const normalizedId = id.toLocaleLowerCase('en-US');
      const previousIndex = usedIds.get(normalizedId);
      if (previousIndex !== undefined) {
        add({
          severity: 'error',
          code: 'field.id.duplicate',
          message: `ID "${id}" is duplicated (first used by field ${previousIndex + 1}).`,
          path: `${path}.id`,
          ...context,
        });
      } else {
        usedIds.set(normalizedId, fieldIndex);
      }
    }

    if (typeof attributes.label !== 'string' || attributes.label.trim() === '') {
      add({
        severity: 'error',
        code: 'field.label.required',
        message: `${type} fields require a label.`,
        path: `${path}.attributes.label`,
        ...context,
      });
    }

    if (type === 'input' || type === 'textarea' || type === 'dropdown') {
      const validations = rawField.validations;
      if (!isRecord(validations) || typeof validations.required !== 'boolean') {
        add({
          severity: 'error',
          code: 'field.required.invalid',
          message: 'The required validation must be a boolean.',
          path: `${path}.validations.required`,
          ...context,
        });
      }
    }

    if (type === 'dropdown') {
      const options = attributes.options;
      if (!Array.isArray(options) || options.length === 0) {
        add({
          severity: 'error',
          code: 'dropdown.options.required',
          message: 'Dropdown fields need at least one non-empty option.',
          path: `${path}.attributes.options`,
          ...context,
        });
      } else {
        const seen = new Set<string>();
        options.forEach((option, optionIndex) => {
          if (typeof option !== 'string' || option.trim() === '') {
            add({
              severity: 'error',
              code: 'dropdown.option.empty',
              message: 'Dropdown options cannot be empty.',
              path: `${path}.attributes.options[${optionIndex}]`,
              ...context,
            });
            return;
          }
          const normalized = option.trim().toLocaleLowerCase('en-US');
          if (seen.has(normalized)) {
            add({
              severity: 'error',
              code: 'dropdown.option.duplicate',
              message: `Dropdown option "${option.trim()}" is duplicated.`,
              path: `${path}.attributes.options[${optionIndex}]`,
              ...context,
            });
          }
          seen.add(normalized);
        });
      }
    }

    if (type === 'checkboxes') {
      const options = attributes.options;
      if (!Array.isArray(options) || options.length === 0) {
        add({
          severity: 'error',
          code: 'checkboxes.options.required',
          message: 'Checkbox fields need at least one option.',
          path: `${path}.attributes.options`,
          ...context,
        });
      } else {
        options.forEach((option, optionIndex) => {
          const optionPath = `${path}.attributes.options[${optionIndex}]`;
          if (!isRecord(option) || typeof option.label !== 'string' || option.label.trim() === '') {
            add({
              severity: 'error',
              code: 'checkboxes.option.label.required',
              message: 'Every checkbox option requires a non-empty label.',
              path: `${optionPath}.label`,
              ...context,
            });
          }
          if (!isRecord(option) || typeof option.required !== 'boolean') {
            add({
              severity: 'error',
              code: 'checkboxes.option.required.invalid',
              message: 'Checkbox option required values must be booleans.',
              path: `${optionPath}.required`,
              ...context,
            });
          }
        });
      }
    }
  });

  return finish(issues);
}

export function validateRepositorySettings(settings: RepositorySettings): ValidationResult {
  const issues: ValidationIssue[] = [];
  const raw = settings as unknown as UnknownRecord;
  if (typeof raw.blankIssuesEnabled !== 'boolean') {
    issues.push({
      severity: 'error',
      code: 'repository.blank_issues_enabled.invalid',
      message: 'blank_issues_enabled must be a boolean.',
      path: 'repositorySettings.blankIssuesEnabled',
    });
  }

  if (!Array.isArray(raw.contactLinks)) {
    issues.push({
      severity: 'error',
      code: 'repository.contact_links.invalid',
      message: 'Contact links must be a list.',
      path: 'repositorySettings.contactLinks',
    });
    return finish(issues);
  }

  raw.contactLinks.forEach((link, index) => {
    const path = `repositorySettings.contactLinks[${index}]`;
    if (!isRecord(link)) {
      issues.push({
        severity: 'error',
        code: 'repository.contact_link.invalid',
        message: 'Each contact link must be an object.',
        path,
      });
      return;
    }
    for (const key of ['name', 'url', 'about'] as const) {
      if (typeof link[key] !== 'string' || link[key].trim() === '') {
        issues.push({
          severity: 'error',
          code: `repository.contact_link.${key}.required`,
          message: `Contact link ${key} is required.`,
          path: `${path}.${key}`,
        });
      }
    }
    if (typeof link.url === 'string' && link.url.trim() !== '') {
      try {
        const url = new URL(link.url);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') throw new Error('protocol');
      } catch {
        issues.push({
          severity: 'error',
          code: 'repository.contact_link.url.invalid',
          message: 'Contact link URL must be a valid http or https URL.',
          path: `${path}.url`,
        });
      }
    }
  });

  return finish(issues);
}

export const validateForm = validateIssueForm;
